import React, { useState, useEffect, useMemo } from 'react';
import HolsterVisualizer from '../HolsterVisualizer';
import ColorSelector from '../ColorSelector';
import OptionSelector from '../OptionSelector';
import SegmentedControl from '../SegmentedControl';
import useConfigShare from '../useConfigShare';
import ShareModal from '../ShareModal';

// --- IMPORTS FOR REUSABLE COMPONENTS ---
import DisclaimerBox from '../DisclaimerBox'; 
import CollapsibleSection from '../CollapsibleSection';

// 1. Import Global Settings (UPDATED WITH LEAD TIME CONSTANTS)
import { 
  ENABLE_RUSH_SHIPPING, 
  RUSH_PRICE, 
  GLOBAL_DISCOUNT_PERCENT, 
  SALE_NAME,
  QS_DAYS, 
  RUSH_DAYS, 
  CUSTOM_DAYS,  
  QS_TIMELINE_TEXT, 
  RUSH_TIMELINE_TEXT, 
  CUSTOM_TIMELINE_TEXT,
  // Lead time labels + colors
  QS_LABEL, CUSTOM_LABEL, QS_COLOR, CUSTOM_COLOR, RUSH_COLOR, leadTimeTint,
  optionLine,
} from '../config/globalSettings';

// 2. Import Data
import { rifleMagData } from '../data/rifleMagData'; 
import '../App.css';

function RifleMagBuilder() {
  // --- STATE ---
  const [viewSide, setViewSide] = useState("front"); 
  const [magId, setMagId] = useState("");
  const [sideId, setSideId] = useState("right"); 
  const [gripId, setGripId] = useState(""); 
  const [colorId, setColorId] = useState("");
  const [hangerId, setHangerId] = useState("no"); // HANGER STATE
  const [mountId, setMountId] = useState("");
  const [breakthroughId, setBreakthroughId] = useState("");
  const [rushService, setRushService] = useState(false);

  // --- SHARE FUNCTIONALITY ---
  const {
    getConfigFromURL, generateShareableURL, saveToLocalStorage, loadFromLocalStorage,
    savedConfigs, saveNamedConfig, deleteNamedConfig, copyToClipboard, copySuccess,
    shareModalOpen, setShareModalOpen,
  } = useConfigShare();

  const getCurrentConfig = () => ({
    magId, sideId, gripId, colorId, hangerId, mountId, breakthroughId, rushService
  });

  const loadConfig = (config) => {
    if (!config) return;
    setMagId(config.magId || '');
    setSideId(config.sideId || 'right'); 
    setGripId(config.gripId || '');
    setColorId(config.colorId || '');
    setHangerId(config.hangerId || 'no'); 
    setMountId(config.mountId || '');
    setBreakthroughId(config.breakthroughId || '');
    setRushService(config.rushService || false);
  };

  useEffect(() => {
    const urlConfig = getConfigFromURL();
    if (urlConfig) {
      loadConfig(urlConfig);
      window.history.replaceState({}, '', window.location.pathname);
      return;
    }
    const savedConfig = loadFromLocalStorage();
    if (savedConfig) { loadConfig(savedConfig); }
  }, []);

  useEffect(() => {
    if (magId) saveToLocalStorage(getCurrentConfig());
  }, [magId, sideId, gripId, colorId, hangerId, mountId, breakthroughId, rushService]);

  const shareableURL = generateShareableURL(getCurrentConfig());

  // --- SELECTIONS ---
  const selectedMag = rifleMagData.mags.find(m => m.id === magId);
  const selectedSide = rifleMagData.sides.find(s => s.id === sideId);
  const selectedGrip = rifleMagData.grips.find(g => g.id === gripId);
  const selectedColor = rifleMagData.colors.find(c => c.id === colorId);
  const selectedHanger = rifleMagData.hangers?.find(h => h.id === hangerId);
  const selectedMount = rifleMagData.mounts.find(m => m.id === mountId);
  const selectedBreakthrough = rifleMagData.breakthrough.find(b => b.id === breakthroughId);

  // --- AUTO-CLEAR HANGER IF NOT AR-15 ---
  useEffect(() => {
    if (magId !== "ar_style" && hangerId !== "no") {
        setHangerId("no");
    }
  }, [magId, hangerId]);

  // --- FILTER MOUNTS ---
  const getFilteredMounts = () => {
    let availableMounts = rifleMagData.mounts;
    
    if (selectedMag && selectedMag.supported_mounts) {
        availableMounts = availableMounts.filter(m => selectedMag.supported_mounts.includes(m.id));
    }
    
    // FILTER: If hanger is yes, only allow DCC mounts AND the monoblock_logo
    if (hangerId === "yes") {
        availableMounts = availableMounts.filter(m => m.id.includes("dcc_") || m.id === "monoblock_logo");
    }

    return availableMounts;
  };
  const filteredMounts = getFilteredMounts();

  // Reset Mount ID if the newly selected mag or hanger does not support the currently selected mount
  useEffect(() => {
    if (mountId && !filteredMounts.find(m => m.id === mountId)) {
        setMountId("");
    }
  }, [magId, hangerId, filteredMounts, mountId]);

  // --- AK IMAGE SWAP LOGIC ---
  const isAKMag = ["ak47_pmag", "ak47_romanian", "ak47_tapco", "ak74", "vepr"].includes(magId);

  const gunDataObj = useMemo(() => {
    let visualizerId = "ar_style"; 
    const magsWithPhotos = ["hk_417"]; 

    if (selectedMag) {
        if (magsWithPhotos.includes(selectedMag.id)) {
            visualizerId = selectedMag.id;
        } else if (isAKMag) {
            visualizerId = "ak47";
        }
    }

    return { 
        id: visualizerId,
        name: selectedMag ? selectedMag.name : "Rifle Mag" 
    };
  }, [selectedMag, isAKMag]);

  // --- DYNAMIC MOUNT IMAGE INTERCEPTION ---
  let visualizerMountData = selectedMount;
  
  if (selectedMount) {
      let tempFront = selectedMount.img_layer_mount;
      let tempBack = selectedMount.img_layer_mount_back;

      if (isAKMag) {
          tempFront = ""; 
          tempBack = tempBack ? tempBack.replace('/rifle_mag/', '/ak_mag/') : "";
      } 
      else if (magId === "ar_style" && hangerId === "yes") {
          tempFront = tempFront ? tempFront.replace('.png', '_hanger.png') : "";
          tempBack = tempBack ? tempBack.replace('.png', '_hanger.png') : "";
      }

      visualizerMountData = {
          ...selectedMount,
          img_layer_mount: tempFront,
          img_layer_mount_back: tempBack
      };
  }

  // --- LEAD TIME LOGIC (UPDATED WITH GLOBALS) ---
  const calculateLeadTime = () => {
    if (!selectedMag) return { text: "Select Options", date: "...", type: "PENDING" };

    let isQuickShip = false;
    if (selectedMag.qs_mag) isQuickShip = true;

    const qsDays = QS_DAYS;       
    const rushDays = RUSH_DAYS;    
    const customDays = CUSTOM_DAYS;  
    
    let targetDays = customDays;
    if (isQuickShip) {
        targetDays = qsDays;
    } else if (rushService) {
        targetDays = rushDays;
    }
    
    const date = new Date();
    let daysAdded = 0;
    while (daysAdded < targetDays) {
      date.setDate(date.getDate() + 1);
      if (date.getDay() !== 0 && date.getDay() !== 6) daysAdded++;
    }
    const dateString = date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });

    if (isQuickShip) {
        return { type: QS_LABEL,    timeline: QS_TIMELINE_TEXT,     date: dateString, color: QS_COLOR, bg: leadTimeTint(QS_COLOR), isCustom: false };
    } 
    if (rushService && ENABLE_RUSH_SHIPPING) {
        return { type: "RUSH PRIORITY", timeline: RUSH_TIMELINE_TEXT,   date: dateString, color: RUSH_COLOR, bg: leadTimeTint(RUSH_COLOR), isCustom: true  };
    }
    return   { type: CUSTOM_LABEL,  timeline: CUSTOM_TIMELINE_TEXT, date: dateString, color: CUSTOM_COLOR, bg: leadTimeTint(CUSTOM_COLOR), isCustom: true  };
  };

  const leadTimeInfo = calculateLeadTime();

  // --- AUTO-CLEAR RUSH WHEN NOT APPLICABLE ---
  useEffect(() => {
    if (rushService && (!leadTimeInfo.isCustom || leadTimeInfo.type === QS_LABEL)) {
        setRushService(false);
    }
  }, [magId, rushService, leadTimeInfo]);

  // --- PRICING ---
  const BASE_PRICE = 44.99; 
  
  const subtotal = 
    BASE_PRICE +
    (selectedColor?.price || 0) +
    (selectedHanger?.price || 0) + 
    (selectedMount?.price || 0) +
    (selectedBreakthrough?.price || 0) +
    (rushService && ENABLE_RUSH_SHIPPING ? RUSH_PRICE : 0);

  const discountAmount = subtotal * GLOBAL_DISCOUNT_PERCENT;
  const total = (subtotal - discountAmount).toFixed(2);

  const isReady = magId && sideId && gripId && colorId && mountId;

  // --- ADD TO CART (UPDATED CART TEXT) ---
  const handleAddToCart = () => {
    if (!isReady) return;

    const configuration = [
        `Mag Model: ${selectedMag?.name}`,
        `Carry Side: ${selectedSide?.name}`,
        `Grip Type: ${selectedGrip?.name}`,
        `Color: ${selectedColor?.name}`,
        magId === "ar_style" ? optionLine("Deep Concealment Hanger", selectedHanger?.name) : null,
        `Mount: ${selectedMount?.name}`,
        optionLine("Breakthrough", selectedBreakthrough?.name),
        `Warranty: Free Lifetime Warranty`,
        `Lead Time: ${leadTimeInfo.timeline}`, 
    ].filter(Boolean).join('\n');

    const productID = 283906; // LIVE RIFLE MAG ID
    
    const params = new URLSearchParams();
    params.append('add-to-cart', productID);
    params.append('quantity', '1');
    params.append('anr_config', configuration);
    params.append('anr_price', total);

    window.location.href = `/checkout/?${params.toString()}`;
  };

  // --- REUSABLE HELPER FOR HEADERS WITH INFO (H3 Level) ---
  const SectionHeader = ({ title, description }) => (
    <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "15px" }}>
        <h3 style={{ textTransform: "uppercase", margin: 0 }}>{title}</h3>
        {description && (
            <div className="tooltip-wrapper">
                <span className="info-icon">i</span>
                <div className="tooltip-content">{description}</div>
            </div>
        )}
    </div>
  );

  return (
    <div className={`builder-container ${colorId ? "sticky-active" : ""}`}>
      <div className="controls-section">
        <h1 className="header-title">Rifle Mag Builder <br/><span style={{fontSize:"16px", color:"#D5A678"}}>TACTICAL & COMPETITION CARRIERS</span></h1>
        
        {/* INPUTS */}
        <OptionSelector label="Select Rifle Mag Model *" options={rifleMagData.mags} selectedValue={magId} onChange={setMagId} />
        
        <h3 style={{ textTransform: "uppercase", marginTop: "30px" }}>Carry Configuration</h3>
        
        <SegmentedControl label="Carry Side" options={rifleMagData.sides} selectedValue={sideId} onChange={setSideId} />
        <div style={{marginBottom:"20px"}}></div>
        <OptionSelector label="Grip Type (Orientation) *" options={rifleMagData.grips} selectedValue={gripId} onChange={setGripId} />

        <hr style={{margin:"30px 0", borderTop:"1px solid #333"}}/>
        
        {/* COLORS WITH TOOLTIP */}
        <SectionHeader 
            title="Color Options" 
            description="ANR Design does not have control over the pattern. The model portrayed in the configurator is a representation." 
        />
        <ColorSelector options={rifleMagData.colors} selectedValue={colorId} onChange={setColorId} />
        
        <hr style={{margin:"30px 0", borderTop:"1px solid #333"}}/>

        {/* --- DEEP CONCEALMENT HANGER (ONLY SHOWS FOR AR-15) --- */}
        {magId === "ar_style" && (
            <>
                <SectionHeader 
                    title="Deep Concealment Hanger" 
                    description={rifleMagData.hangers?.find(h => h.id === "yes")?.description} 
                />
                <OptionSelector 
                    label="Optional Hanger System (Requires DCC Clip) *" 
                    options={rifleMagData.hangers || []} 
                    selectedValue={hangerId} 
                    onChange={setHangerId} 
                />
                <hr style={{margin:"30px 0", borderTop:"1px solid #333"}}/>
            </>
        )}

        {/* MOUNTING WITH MEGA TOOLTIP */}
        <SectionHeader 
            title="Mounting Options" 
            description={
                <div className="mega-tooltip">
                    {filteredMounts.map(mount => (
                        <div key={mount.id} className="mount-info-row" style={{marginBottom: '10px', paddingBottom: '10px', borderBottom: '1px solid #555'}}>
                            <div className="mount-text">
                                <h4 style={{marginTop: 0, marginBottom: '5px'}}>{mount.name}</h4>
                                {mount.description && <p style={{margin: 0}}>{mount.description}</p>}
                            </div>
                        </div>
                    ))}
                </div>
            } 
        />
        <OptionSelector label="Select Mount *" options={filteredMounts} selectedValue={mountId} onChange={setMountId} />

        {/* --- FREE LIFETIME WARRANTY BANNER --- */}
        <div style={{ marginTop: "30px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginBottom: "10px" }}>
                <h3 style={{ textTransform: "uppercase", margin: 0 }}>Lifetime Warranty</h3>
                <div className="tooltip-wrapper">
                    <span className="info-icon">i</span>
                    <div className="tooltip-content">
                        Every ANR Design holster is backed by an automatic Lifetime Warranty on the Kydex® shell for the original purchaser. If your shell cracks or breaks under normal, intended use, we replace it. Consumables (hardware, loops, spacers), cosmetic wear, and damage from modifications or extreme heat are not covered. Return shipping fees apply.
                        <br/><br/>
                        <a href="https://www.anrkydexholsters.com/warranty-privacy-and-shipping-policy/" target="_blank" rel="noopener noreferrer" style={{ color: "#D5A678", textDecoration: "underline", fontWeight: "bold" }}>
                            Learn More & File a Claim
                        </a>
                    </div>
                </div>
            </div>
            <div style={{
                padding: "15px",
                backgroundColor: "rgba(76, 175, 80, 0.1)",
                borderLeft: "4px solid #4caf50",
                borderRadius: "4px",
                fontSize: "14px",
                color: "#4caf50",
                fontWeight: "600"
            }}>
                ✓ Free Lifetime Warranty Included
            </div>
        </div>

        <h3 style={{ textTransform: "uppercase", marginTop: "30px" }}>Add Breakthrough Products</h3>
        <OptionSelector label="Optional firearm maintenance" options={rifleMagData.breakthrough} selectedValue={breakthroughId} onChange={setBreakthroughId} />

        <hr style={{margin:"50px 0 20px 0", borderTop:"1px solid #333"}}/>

        {/* --- COLLAPSIBLE DESCRIPTION --- */}
        <CollapsibleSection title="Description">
            <p style={{ marginTop: 0 }}>
                Using our custom Rifle Mag Carrier, comfortably carry your backup magazines. Our single KYDEX® Thermoplastics rifle mag carriers provide you with increased durability and perfect retention needed for all applications. Made with the highest quality materials available, our KYDEX® Thermoplastics magazine carriers are built for hard use, standing up to the punishment of our sponsored competition shooters and everyday users. We offer the most popular selection of AR15 and AK47 carriers available and rest assured these carriers are produced using the same methods as our KYDEX® Thermoplastics holsters insuring each carrier is tested and retested before leaving the shop. Each Rifle Mag Carrier is custom made to order and handcrafted in the USA by a trusted brand.
            </p>
        </CollapsibleSection>

        {/* COLLAPSIBLE WARNINGS */}
        <div className="mobile-only-content">
            <CollapsibleSection title="Important Notes & Warnings">
                <DisclaimerBox mode="mobile" setShareModalOpen={setShareModalOpen} gunId={magId} />
            </CollapsibleSection>
        </div>

      </div>

      <div className="preview-section">
        <div className="sticky-wrapper">

          <HolsterVisualizer 
            holsterType="rifle_mag" 
            gunData={gunDataObj} 
            colorData={selectedColor} 
            mountData={visualizerMountData}
            hangerData={magId === "ar_style" ? selectedHanger : null} 
            isLightbearing="no" 
            viewSide={viewSide} 
          />

          {/* --- FRONT / REAR TOGGLE --- */}
          <div style={{ display: "flex", justifyContent: "center", marginTop: "15px", marginBottom: "15px" }}>
            <div style={{ display: "flex", backgroundColor: "#333", borderRadius: "4px", padding: "4px", gap: "5px" }}>
              <button onClick={() => setViewSide("front")} style={{ background: viewSide === "front" ? "#D5A678" : "transparent", color: viewSide === "front" ? "#000" : "#888", border: "none", padding: "6px 12px", borderRadius: "4px", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>FRONT</button>
              <button onClick={() => setViewSide("back")} style={{ background: viewSide === "back" ? "#D5A678" : "transparent", color: viewSide === "back" ? "#000" : "#888", border: "none", padding: "6px 12px", borderRadius: "4px", fontWeight: "bold", fontSize: "11px", cursor: "pointer" }}>REAR</button>
            </div>
          </div>

          {/* --- MOVED LEAD TIME BOX --- */}
          {leadTimeInfo.type !== "PENDING" && (
             <div className="lead-time-box" style={{ 
                 backgroundColor: leadTimeInfo.bg, 
                 padding: "15px", 
                 borderLeft: `4px solid ${leadTimeInfo.color}`, 
                 marginBottom: "20px"
             }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "start" }}>
                    <div>
                        {leadTimeInfo.type && <strong style={{ color: leadTimeInfo.color, textTransform: "uppercase", fontSize: "16px" }}>{leadTimeInfo.type}</strong>}
                        <div style={{ marginTop: "5px", color: "#fff" }}>
                        Lead Time: <strong style={{ color: leadTimeInfo.color }}>{leadTimeInfo.timeline}</strong><br/>
                        Est. Ship Date: <strong>{leadTimeInfo.date}</strong>
                        </div>
                    </div>
                    
                    {/* RUSH TOGGLE */}
                    {leadTimeInfo.isCustom && ENABLE_RUSH_SHIPPING && (
                        <div style={{ marginLeft: "15px" }}>
                            <label style={{ 
                                display: "flex", alignItems: "center", cursor: "pointer",
                                background: rushService ? "#D5A678" : "transparent",
                                border: "1px solid #d32f2f", padding: "12px 20px", borderRadius: "4px",
                                transition: "all 0.2s"
                            }}>
                                <input 
                                    type="checkbox" 
                                    checked={rushService} 
                                    onChange={(e) => setRushService(e.target.checked)}
                                    style={{ marginRight: "8px", accentColor: "#000" }} 
                                />
                                <div style={{ lineHeight: "1.7" }}>
                                    <div style={{ fontSize: "15px", fontWeight: "bold", color: rushService ? "#000" : "#D5A678", textTransform: "uppercase" }}>RUSH MY BUILD</div>
                                    <div style={{ fontSize: "15px", color: rushService ? "#000" : "#ccc" }}>+${RUSH_PRICE.toFixed(2)}</div>
                                </div>
                            </label>
                        </div>
                    )}
                </div>
             </div>
          )}
          {leadTimeInfo.type === "PENDING" && (
            <div style={{ textAlign: "center", marginTop: "15px", color: "#666", fontSize: "12px", marginBottom: "15px" }}>Select options to see lead time</div>
          )}
          
          <div className="price-tag">
            {GLOBAL_DISCOUNT_PERCENT > 0 ? (
                <>
                <span style={{ textDecoration: "line-through", color: "#888", marginRight: "10px", fontSize: "0.8em" }}>${subtotal.toFixed(2)}</span>
                <span style={{ color: "#00ff00" }}>${total}</span>
                <div style={{ fontSize: "10px", color: "#00ff00", textTransform: "uppercase", letterSpacing: "1px", marginTop: "5px" }}>
                    {SALE_NAME} ACTIVE
                </div>
                </>
            ) : (
                `Total: $${total}`
            )}
          </div>

          <DisclaimerBox mode="desktop" setShareModalOpen={setShareModalOpen} gunId={magId} />

          <button className={`add-to-cart-btn ${isReady ? "pulse-active" : ""}`} disabled={!isReady} onClick={handleAddToCart}>{isReady ? "ADD TO CART" : "SELECT OPTIONS"}</button>
        </div>
      </div>
      
      <div className="mobile-action-bar mobile-only-content">
        <div className="mobile-price-container"><span className="mobile-label">Estimated Total</span><span className="mobile-total">${total}</span></div>
        <button className={`mobile-cart-btn ${isReady ? "pulse-active" : ""}`} disabled={!isReady} onClick={handleAddToCart}>{isReady ? "ADD TO CART" : "INCOMPLETE"}</button>
      </div>

      <ShareModal isOpen={shareModalOpen} onClose={() => setShareModalOpen(false)} shareableURL={shareableURL} currentConfig={getCurrentConfig()} savedConfigs={savedConfigs} onSaveConfig={saveNamedConfig} onLoadConfig={loadConfig} onDeleteConfig={deleteNamedConfig} onCopyLink={copyToClipboard} copySuccess={copySuccess} gunName={selectedMag?.name} colorName={selectedColor?.name} />
    </div>
  );
}

export default RifleMagBuilder;